/* eslint-disable */
import React from 'react';
import { makeStyles, RACButton, RACCard } from '@rentacenter/racstrap';
import clsx from 'clsx';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { useNationalPartnerData, useNationalPartnerActions } from './NationalPartnerProvider';
import { useVendorPopup } from './useVendorPopup';
import { deriveCartUrl, buildAgreementNavigationState, buildAgreementPath } from './navigationHelpers';

/**
 * Location state passed when navigating to Local Sourcing from customer search/update.
 */
interface LocalSourcingLocationState {
  customerId?: string;
  globalCustomerId?: string;
  employeeId?: string;
  approvalLevel?: string;
}

const useStyles = makeStyles(() => ({
  footerRoot: {
    width: '100%',
    marginBottom: '0rem',
    display: 'block',
    position: 'fixed',
    bottom: '0',
    left: '0',
    zIndex: 1100,
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '0rem',
  },
  cardBody: {
    flex: '0 0 auto',
    padding: '0.75rem 1.5rem',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cancelButton: {
    textTransform: 'none',
  },
  actionButton: {
    textTransform: 'none',
    fontFamily: 'OpenSans-semibold',
    fontSize: '0.9375rem',
    padding: '0.5rem 1.5rem',
  },
}));

export const LocalSourcingFooter: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation<LocalSourcingLocationState>();
  const params = useParams<{ customerId?: string }>();

  // Location state from customer navigation (passed by UpdateCustomerFooter / SearchCustomerResult)
  const locationState = location.state || {};
  
  // Capture customerId once on mount and persist it — location.state can be lost during internal navigations
  const customerIdRef = React.useRef<string>(
    locationState.customerId || params.customerId || ''
  );
  if (!customerIdRef.current && (locationState.customerId || params.customerId)) {
    customerIdRef.current = locationState.customerId || params.customerId || '';
  }
  const customerId = customerIdRef.current;

  // Debug: log what's available on mount
  React.useEffect(() => {
    console.log('[LocalSourcingFooter] location.state:', location.state);
    console.log('[LocalSourcingFooter] params:', params);
    console.log('[LocalSourcingFooter] resolved customerId:', customerId);
  }, []);

  const { fundingState, checkoutState, externalLaunchUrl, spendLimitId, storeNumber, vendorName, vendorId, supplierName, supplierOrderNumber, resolvedProducts } = useNationalPartnerData();
  const { setCheckoutState } = useNationalPartnerActions();
  const { openPopup, isOpen: isPopupOpen } = useVendorPopup();

  // Button states
  const showContinueCheckout = checkoutState !== 'PURCHASE_SUCCESSFUL';
  const continueCheckoutEnabled = fundingState === 'APPROVED' && checkoutState === 'NONE';
  const showJumpToAgreement = checkoutState === 'PURCHASE_SUCCESSFUL';

  // Track popup close → trigger verification
  const prevIsPopupOpenRef = React.useRef<boolean>(false);
  React.useEffect(() => {
    if (prevIsPopupOpenRef.current === true && isPopupOpen === false && checkoutState === 'IN_PROGRESS') {
      setCheckoutState('VERIFYING_PURCHASE');
    }
    prevIsPopupOpenRef.current = isPopupOpen;
  }, [isPopupOpen, checkoutState, setCheckoutState]);

  const handleCancel = () => {
    history.goBack();
  };

  const handleContinueCheckout = () => {
    console.log('[LocalSourcing] handleContinueCheckout called - externalLaunchUrl:', externalLaunchUrl, 'fundingState:', fundingState, 'checkoutState:', checkoutState);
    if (!externalLaunchUrl) {
      console.error('[LocalSourcing] Continue Checkout blocked — externalLaunchUrl is empty');
      return;
    }
    const cartUrl = deriveCartUrl(externalLaunchUrl);
    const success = openPopup(cartUrl);
    console.log('[LocalSourcing] openPopup result:', success, 'cartUrl:', cartUrl);
    if (success) {
      setCheckoutState('IN_PROGRESS');
    }
  };

  const handleJumpToAgreement = () => {
    if (!customerId) {
      console.error('[LocalSourcing] Cannot navigate — customerId not available in location state or route params');
      return;
    }

    console.log('[LocalSourcing] Jump to Agreement - resolvedProducts:', JSON.stringify(resolvedProducts, null, 2));
    console.log('[LocalSourcing] Jump to Agreement - context:', JSON.stringify({ spendLimitId, storeNumber, vendorName, vendorId, supplierName, supplierOrderNumber }, null, 2));

    const result = buildAgreementNavigationState({
      spendLimitId,
      storeNumber,
      vendorName,
      vendorId,
      supplierName,
      supplierOrderNumber,
      resolvedProducts,
    });

    if ('error' in result) {
      console.error('[LocalSourcing] Navigation state error:', result.error);
      return;
    }

    const navigationPath = buildAgreementPath(customerId, spendLimitId);

    console.log('[LocalSourcing] Jump to Agreement - customerId:', customerId);
    console.log('[LocalSourcing] Jump to Agreement - path:', navigationPath);
    console.log('[LocalSourcing] Jump to Agreement - state:', JSON.stringify(result, null, 2));

    history.push(navigationPath, result);
  };

  return (
    <div className={clsx(classes.footerRoot)}>
      <RACCard className={classes.card}>
        <div className={clsx(classes.cardBody)}>
          <div className={clsx(classes.buttonContainer)}>
            <RACButton
              {...({ variant: 'outlined', color: 'primary' } as any)}
              className={classes.cancelButton}
              onClick={handleCancel}
            >
              {'Cancel'}
            </RACButton>

            {showContinueCheckout && (
              <RACButton
                {...({ variant: 'contained', color: 'primary' } as any)}
                className={classes.actionButton}
                disabled={!continueCheckoutEnabled}
                onClick={handleContinueCheckout}
              >
                {'Continue Checkout'}
              </RACButton>
            )}

            {showJumpToAgreement && (
              <RACButton
                {...({ variant: 'contained', color: 'primary' } as any)}
                className={classes.actionButton}
                onClick={handleJumpToAgreement}
              >
                {'Continue to agreement'}
              </RACButton>
            )}
          </div>
        </div>
      </RACCard>
    </div>
  );
};
